import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  TextField,
  CircularProgress,
  Collapse,
  Button,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ImageOutlined as ImageIcon,
  CheckCircleOutline as ShownIcon,
  HighlightOff as NotShownIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDevice } from '../contexts/DeviceContext';
import { impactDetectionService } from '../services/impactDetectionService';
import {
  ImpactDetectionSettings,
  ImpactDetectionSettingsUpdate,
  ImpactSensitivity,
  ImpactEvent,
  DEFAULT_IMPACT_DETECTION_SETTINGS,
} from '../types/impactDetection';
import { Action } from '../types/action';
import { ActionPicker } from './schedules/ActionPicker';

const SENSITIVITY_LEVELS: ImpactSensitivity[] = ['low', 'medium', 'high'];

const ImpactDetectionSection: React.FC = () => {
  const { t } = useTranslation();
  const { selectedDevice, showSnackbar, deviceActions } = useDevice();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<ImpactDetectionSettings>(
    DEFAULT_IMPACT_DETECTION_SETTINGS
  );
  const [durationInput, setDurationInput] = useState<string>('5');
  const [actionPickerOpen, setActionPickerOpen] = useState(false);
  const [events, setEvents] = useState<ImpactEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  const isViewer = selectedDevice?.role === 'viewer';

  const selectedAction: Action | undefined = settings.actionId
    ? deviceActions.find((a: Action) => a.id === settings.actionId)
    : undefined;

  // Load settings
  const loadSettings = useCallback(async () => {
    if (!selectedDevice?.id) return;

    try {
      setLoading(true);
      const data = await impactDetectionService.getSettings(selectedDevice.id);
      setSettings(data);
      setDurationInput(String(data.overlayDurationMinutes));
    } catch (error) {
      console.error('Failed to load impact detection settings:', error);
      showSnackbar(t('impactDetection.failedToLoad'), 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedDevice?.id, showSnackbar, t]);

  // Load event history
  const loadEvents = useCallback(async () => {
    if (!selectedDevice?.id) return;

    try {
      setEventsLoading(true);
      const data = await impactDetectionService.getEvents(selectedDevice.id);
      setEvents(data);
    } catch (error) {
      console.error('Failed to load impact events:', error);
      showSnackbar(t('impactDetection.failedToLoadEvents'), 'error');
    } finally {
      setEventsLoading(false);
    }
  }, [selectedDevice?.id, showSnackbar, t]);

  useEffect(() => {
    loadSettings();
    loadEvents();
  }, [loadSettings, loadEvents]);

  // Save settings
  const saveSettings = useCallback(
    async (updates: ImpactDetectionSettingsUpdate) => {
      if (!selectedDevice?.id || isViewer) return;

      try {
        setSaving(true);
        const updatedSettings = await impactDetectionService.updateSettings(
          selectedDevice.id,
          updates
        );
        setSettings(updatedSettings);
        setDurationInput(String(updatedSettings.overlayDurationMinutes));
        showSnackbar(t('impactDetection.settingsSaved'), 'success');
      } catch (error) {
        console.error('Failed to save impact detection settings:', error);
        showSnackbar(t('impactDetection.failedToSave'), 'error');
      } finally {
        setSaving(false);
      }
    },
    [selectedDevice?.id, isViewer, showSnackbar, t]
  );

  const handleEnabledChange = (enabled: boolean) => {
    setSettings((prev) => ({ ...prev, enabled }));
    saveSettings({ enabled });
  };

  const handleSensitivityChange = (sensitivity: ImpactSensitivity) => {
    setSettings((prev) => ({ ...prev, sensitivity }));
    saveSettings({ sensitivity });
  };

  // Duration is edited locally and saved on blur to avoid a request per keystroke
  const handleDurationBlur = () => {
    const parsed = parseInt(durationInput, 10);
    if (isNaN(parsed) || parsed < 1 || parsed > 180) {
      setDurationInput(String(settings.overlayDurationMinutes));
      return;
    }
    if (parsed !== settings.overlayDurationMinutes) {
      setSettings((prev) => ({ ...prev, overlayDurationMinutes: parsed }));
      saveSettings({ overlayDurationMinutes: parsed });
    }
  };

  const handleSelectAction = (action: Action) => {
    setSettings((prev) => ({ ...prev, actionId: action.id }));
    saveSettings({ actionId: action.id });
  };

  const handleClearAction = () => {
    setSettings((prev) => ({ ...prev, actionId: null }));
    saveSettings({ actionId: '' });
  };

  if (!selectedDevice) return null;

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('impactDetection.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('impactDetection.description')}
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Enable toggle */}
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.enabled}
                    onChange={(e) => handleEnabledChange(e.target.checked)}
                    disabled={isViewer || saving}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">{t('impactDetection.enable')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('impactDetection.enableDescription')}
                    </Typography>
                  </Box>
                }
              />
            </Box>

            <Collapse in={settings.enabled}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Sensitivity */}
                <FormControl fullWidth disabled={isViewer || saving}>
                  <InputLabel>{t('impactDetection.sensitivity')}</InputLabel>
                  <Select
                    value={settings.sensitivity}
                    onChange={(e) =>
                      handleSensitivityChange(e.target.value as ImpactSensitivity)
                    }
                    label={t('impactDetection.sensitivity')}
                  >
                    {SENSITIVITY_LEVELS.map((level) => (
                      <MenuItem key={level} value={level}>
                        {t(`impactDetection.sensitivityLevels.${level}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Overlay duration */}
                <TextField
                  fullWidth
                  type="number"
                  label={t('impactDetection.overlayDuration')}
                  value={durationInput}
                  onChange={(e) => setDurationInput(e.target.value)}
                  onBlur={handleDurationBlur}
                  disabled={isViewer || saving}
                  inputProps={{ min: 1, max: 180 }}
                  helperText={t('impactDetection.overlayDurationHelp')}
                />

                {/* Action picker */}
                <Box>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {t('impactDetection.action')}
                  </Typography>
                  {selectedAction ? (
                    <Card
                      variant="outlined"
                      sx={{ cursor: isViewer ? 'default' : 'pointer' }}
                      onClick={() => !isViewer && setActionPickerOpen(true)}
                    >
                      <CardContent sx={{ display: 'flex', alignItems: 'center', py: 1.5 }}>
                        {selectedAction.thumbnailUrl || selectedAction.pictureUrl ? (
                          <Box
                            component="img"
                            src={selectedAction.thumbnailUrl || selectedAction.pictureUrl || ''}
                            alt={selectedAction.name}
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 1,
                              objectFit: 'cover',
                              mr: 2,
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 1,
                              bgcolor: 'grey.100',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2,
                            }}
                          >
                            <ImageIcon sx={{ color: 'grey.400' }} />
                          </Box>
                        )}
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="body1" noWrap>
                            {selectedAction.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {selectedAction.description}
                          </Typography>
                        </Box>
                        {!isViewer && (
                          <Button
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClearAction();
                            }}
                            disabled={saving}
                          >
                            {t('impactDetection.clearAction')}
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  ) : (
                    <Box>
                      {settings.actionId && (
                        <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                          {t('impactDetection.actionMissing')}
                        </Typography>
                      )}
                      <Button
                        variant="outlined"
                        onClick={() => setActionPickerOpen(true)}
                        disabled={isViewer || saving}
                      >
                        {t('impactDetection.chooseAction')}
                      </Button>
                      {!settings.actionId && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {t('impactDetection.noActionHint')}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </Collapse>

            {/* Saving indicator */}
            {saving && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  {t('common.saving')}
                </Typography>
              </Box>
            )}

            {/* Event history */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1">
                  {t('impactDetection.history')}
                </Typography>
                <IconButton
                  size="small"
                  onClick={loadEvents}
                  disabled={eventsLoading}
                  aria-label={t('impactDetection.refresh')}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
                {eventsLoading && <CircularProgress size={16} />}
              </Box>

              {!eventsLoading && events.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('impactDetection.historyEmpty')}
                </Typography>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('impactDetection.eventTime')}</TableCell>
                        <TableCell>{t('impactDetection.eventMagnitude')}</TableCell>
                        <TableCell>{t('impactDetection.sensitivity')}</TableCell>
                        <TableCell>{t('impactDetection.eventOverlayShown')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {events.map((event) => (
                        <TableRow key={event.id}>
                          <TableCell>
                            {new Date(event.timestamp).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            {(event.peak_magnitude / 9.81).toFixed(1)} g
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={t(`impactDetection.sensitivityLevels.${event.sensitivity}`, event.sensitivity)}
                            />
                          </TableCell>
                          <TableCell>
                            {event.action_triggered ? (
                              <ShownIcon fontSize="small" color="success" />
                            ) : (
                              <NotShownIcon fontSize="small" color="disabled" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </CardContent>

      {/* Action picker dialog */}
      <ActionPicker
        open={actionPickerOpen}
        onClose={() => setActionPickerOpen(false)}
        onSelect={handleSelectAction}
        actions={deviceActions}
        title={t('impactDetection.chooseAction')}
      />
    </Card>
  );
};

export default ImpactDetectionSection;
